import {
  Home,
  UserRole,
  CernerRole,
  ResidentStatus,
  ResidentItem,
  User
} from '@lmc/ts-common'
import { Icon, IconType } from '@lmc/ui-kit'
import { getNominative } from 'common/utils'

export const getResidentSideNavItems = ({
  features,
  carePlanNominative,
  hasLegacyMedications,
  hasGPConnectEnabled,
  hasGPConnectClinicalAccessUser,
  hasRota,
  hasRiskAssessment,
  hasSharedCareRecord,
  hasGoals,
  communicationRecordsEnabled,
  emarV2StockEnabled,
  user,
  resident
}: {
  features: Home
  carePlanNominative?: string
  hasLegacyMedications?: boolean
  hasGPConnectEnabled?: boolean
  hasGPConnectClinicalAccessUser?: boolean
  hasRota?: boolean
  hasRiskAssessment?: boolean
  hasSharedCareRecord?: boolean
  hasGoals?: boolean
  communicationRecordsEnabled?: boolean
  emarV2StockEnabled?: boolean
  user?: User
  resident?: ResidentItem
}) => {
  let NAV_ITEMS = [
    {
      label: 'Profile',
      url: 'profile-route',
      icon: <Icon customIcon={IconType.SideProfile} />,
      subItems: [
        {
          label: 'General Information',
          url: 'profile'
        },
        ...(features?.pro
          ? [
              {
                label: 'Needs',
                url: 'needs'
              }
            ]
          : []),
        {
          label: 'Important People',
          url: 'important-people'
        },
        {
          label: 'About Me',
          url: 'about-me'
        },
        ...(!hasRota && features?.pro
          ? [
              {
                label: 'Future Plans',
                url: 'future-plans',
                id: 'contingency-plans-menu-item',
                'data-intercom-target': 'contingency-plans-menu-item'
              }
            ]
          : []),
        ...(hasLegacyMedications && !features.features?.eMAR?.enabled
          ? [{ label: 'Medications', url: 'profile/medications' }]
          : []),
        ...(hasRota && features?.pro
          ? [
              {
                label: 'Care Package',
                url: 'care-package-and-patterns',
                id: 'care-package-and-patterns-menu-item',
                'data-intercom-target': 'care-package-and-patterns-menu-item'
              }
            ]
          : [])
      ]
    },
    {
      label: 'Daily Logs',
      url: 'daily-report',
      icon: <Icon customIcon={IconType.SideLogs} />
    },
    {
      label: 'Charts',
      url: 'charts-route',
      icon: <Icon customIcon={IconType.SideCharts} />,
      subItems: [
        {
          label: 'General Charts',
          url: 'charts',
          icon: <Icon customIcon={IconType.SideCharts} />
        },
        {
          label: 'Risk Assessment Scores',
          url: 'risk-assessment-scores',
          icon: <Icon customIcon={IconType.SideCharts} />
        }
      ]
    },
    {
      label: 'Documents',
      url: 'documents',
      icon: <Icon customIcon={IconType.SideDocuments} />
    },
    {
      label: 'Care Planning',
      url: 'care-plans-route',
      icon: <Icon customIcon={IconType.SidePlanning} />,
      subItems: [
        {
          label: getNominative(carePlanNominative, 'Care Plans'),
          url: 'care-plans'
        },
        ...(hasRota && features.pro
          ? [
              {
                label: 'Future Plans',
                url: 'future-plans'
              }
            ]
          : []),
        {
          label: 'Initial Assessment',
          url: 'initial-assessments'
        },
        ...(hasRiskAssessment
          ? [
              {
                label: 'Risk Assessments',
                url: 'risk-assessments'
              }
            ]
          : []),
        ...(hasGoals
          ? [
              {
                label: 'Goals',
                url: 'goals'
              }
            ]
          : [])
      ]
    },

    {
      label: 'Medication',
      url: features.features?.eMARStock?.enabled ? 'emar-route' : 'emar',
      icon: <Icon customIcon={IconType.SideMedication} />,
      ...(features.features?.eMARStock?.enabled && {
        subItems: [
          { label: 'MAR Chart', url: 'emar' },
          { label: 'Stock', url: 'emar/stock' },
          ...(emarV2StockEnabled
            ? [{ label: 'Medication List', url: 'emar/medication-list' }]
            : [])
        ]
      })
    }
  ] as any

  NAV_ITEMS = [
    ...NAV_ITEMS,
    {
      label: 'Consent',
      url: 'consent',
      icon: <Icon customIcon={IconType.SideConsent} />
    }
  ]

  if (hasSharedCareRecord) {
    const hasSharedCareRecordEnabled = !!features?.odsCode
    const hasSharedCareRecordPermission =
      user?.role === UserRole.seniorCarer ||
      user?.role === UserRole.careHomeAdmin
    const hasCernerRolePermission =
      user?.cernerRole &&
      [
        CernerRole.SeniorCarer,
        CernerRole.Admin,
        CernerRole.RegisteredManager
      ].includes(user.cernerRole)
    const isResidentActive = resident?.status === ResidentStatus.active
    const hasNhsNumber = !!resident?.nhsNumber

    const shouldShowSharedCareRecord =
      hasSharedCareRecord &&
      hasSharedCareRecordEnabled &&
      hasSharedCareRecordPermission &&
      hasCernerRolePermission &&
      isResidentActive &&
      hasNhsNumber

    if (shouldShowSharedCareRecord) {
      NAV_ITEMS = [
        ...NAV_ITEMS,
        {
          label: '',
          url: 'shared-care-record',
          icon: (
            <img
              height={21}
              src="https://lmc-marketing-public.s3.eu-west-2.amazonaws.com/OneLondon_LCR+logo_Blue+(1).png"
              alt="Shared Care Record"
            />
          )
        }
      ]
    }
  }

  if (communicationRecordsEnabled && user?.role !== UserRole.family) {
    NAV_ITEMS = [
      ...NAV_ITEMS,
      {
        label: 'Health Records',
        url: 'health-records',
        icon: <Icon customIcon={IconType.HealthRecords} />,
        subItems: [
          {
            label: 'Communication Records',
            url: 'communication-records'
          }
        ]
      }
    ]
  }

  NAV_ITEMS = [
    ...NAV_ITEMS,
    ...(hasGPConnectEnabled
      ? [
          {
            label: 'GP Connect',
            url: 'gp-connect-route',
            icon: <Icon customIcon={IconType.NhsIcon} />,
            subItems: [
              { label: 'Summary', url: 'gp-connect/sum' },
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Encounters', url: 'gp-connect/enc' }]
                : []),
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Clinical Items', url: 'gp-connect/cli' }]
                : []),
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Problems and Issues', url: 'gp-connect/prb' }]
                : []),
              {
                label: 'Allergies and Adverse Reactions',
                url: 'gp-connect/all'
              },
              { label: 'Medications', url: 'gp-connect/med' },
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Referrals', url: 'gp-connect/ref' }]
                : []),
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Observations', url: 'gp-connect/obs' }]
                : []),
              { label: 'Immunisations', url: 'gp-connect/imm' },
              ...(hasGPConnectClinicalAccessUser
                ? [{ label: 'Administrative Items', url: 'gp-connect/adm' }]
                : [])
            ]
          }
        ]
      : [])
  ]

  return NAV_ITEMS
}
